import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Sidebar from '@/components/Sidebar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditCar() {
  const { id } = useParams();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [video, setVideo] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [currentVideo, setCurrentVideo] = useState(null);
  const [fields, setFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCar();
    fetchFields();
  }, [id]);

  const fetchCar = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/cars/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const car = response.data;
      setTitle(car.title);
      setDescription(car.description || '');
      setCurrentVideo(car.video_url);
      
      // Convert field values array to object
      const fvObj = {};
      car.field_values.forEach((fv) => {
        fvObj[fv.field_id] = fv.value;
      });
      setFieldValues(fvObj);
    } catch (error) {
      toast.error('Failed to fetch car details');
      navigate('/dashboard');
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API}/fields`);
      setFields(response.data);
    } catch (error) {
      toast.error('Failed to fetch fields');
    }
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 100 * 1024 * 1024) {
        toast.error('File size exceeds 100MB limit');
        return;
      }
      setVideo(file);
      setVideoPreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('description', description);
      if (video) {
        formData.append('video', video);
      }
      
      const fieldValuesArray = Object.entries(fieldValues).map(([fieldId, value]) => ({
        field_id: fieldId,
        value: value,
      }));
      formData.append('field_values', JSON.stringify(fieldValuesArray));

      const token = localStorage.getItem('token');
      await axios.put(`${API}/cars/${id}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Car updated successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update car');
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <div className="flex min-h-screen bg-zinc-950" data-testid="edit-car-loading">
        <Sidebar />
        <main className="flex-1 p-8 flex items-center justify-center">
          <div className="text-white">Loading car details...</div>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-zinc-950" data-testid="edit-car-page">
      <Sidebar />
      <main className="flex-1 p-8">
        <div className="max-w-4xl mx-auto">
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="mb-6 text-zinc-400 hover:text-white hover:bg-zinc-800"
            data-testid="back-button"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>

          <h1 className="text-4xl font-bold text-white mb-8" data-testid="edit-car-title">Edit Car</h1>

          <form onSubmit={handleSubmit} data-testid="edit-car-form">
            <Card className="border-zinc-800 bg-zinc-900/50 mb-6">
              <CardContent className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-zinc-300">Title *</Label>
                  <Input
                    id="title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    required
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500"
                    data-testid="title-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-zinc-300">Description</Label>
                  <Textarea
                    id="description"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500 min-h-[120px]"
                    data-testid="description-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300">Current Video</Label>
                  {currentVideo && (
                    <div className="rounded-lg overflow-hidden bg-zinc-800" data-testid="current-video">
                      <video src={currentVideo} controls className="w-full max-h-80" />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="video" className="text-zinc-300">Replace Video (Max 100MB)</Label>
                  <Input
                    id="video"
                    type="file"
                    accept=".mp4,.webm,.mov"
                    onChange={handleVideoChange}
                    className="bg-zinc-800 border-zinc-700 text-white file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer"
                    data-testid="video-input"
                  />
                  {videoPreview && (
                    <div className="mt-4 rounded-lg overflow-hidden bg-zinc-800" data-testid="video-preview">
                      <video src={videoPreview} controls className="w-full max-h-80" />
                    </div>
                  )}
                </div>

                {fields.length > 0 && (
                  <div className="pt-6 border-t border-zinc-800">
                    <h3 className="text-lg font-semibold text-white mb-4" data-testid="custom-fields-title">Custom Fields</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {fields.map((field) => (
                        <div key={field.id} className="space-y-2">
                          <Label htmlFor={field.id} className="text-zinc-300">
                            {field.field_name} {field.is_required && '*'}
                          </Label>
                          <Input
                            id={field.id}
                            type={field.field_type === 'number' ? 'number' : 'text'}
                            value={fieldValues[field.id] || ''}
                            onChange={(e) => setFieldValues({ ...fieldValues, [field.id]: e.target.value })}
                            required={field.is_required}
                            className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-blue-500"
                            data-testid={`field-input-${field.id}`}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <div className="flex gap-4">
              <Button
                type="submit"
                disabled={loading}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6"
                data-testid="submit-button"
              >
                {loading ? 'Updating Car...' : 'Update Car'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 hover:text-white px-8"
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
}
